<template>
    <card-normal>
        <template #sections>
            <card-section area="card-header">
                <text-decorator bold>
                    Financieel
                </text-decorator>
            </card-section>

            <card-section v-if="props.editable" area="card-footer">
                <flex-container :gap="Size.sm" justify-content="end">
                    <button-outline @click="onOrderReset">
                        Annuleren
                    </button-outline>

                    <button-block :disabled="!orderCalculation.isAtTotalValue.value" @loading="onOrderSave">
                        Opslaan
                    </button-block>
                </flex-container>
            </card-section>
        </template>

        <flex-container v-if="localOrder" :gap="Size.sm">
            <text-heading sub>
                Omzet en transactie
            </text-heading>

            <flex-container>
                <two-columns class="financial-tab-panel__fields">
                    <input-group
                        :model-value="formatFloatValue(localOrder.askingPrice)"
                        @update:model-value="localOrder.askingPrice = parseFloatValue($event)"
                    >
                        <template #label>
                            <input-label>Vraagprijs</input-label>
                        </template>
                        <template #input>
                            <text-input :disabled="!props.editable">
                                <template #prefix>
                                    €
                                </template>
                            </text-input>
                        </template>
                    </input-group>

                    <input-group
                        :model-value="formatFloatValue(localOrder.orderValue)"
                        @update:model-value="updateOrderValue(parseFloatValue($event))"
                    >
                        <template #label>
                            <input-label>Totale omzet/courtage</input-label>
                        </template>
                        <template #input>
                            <text-input :disabled="!props.editable || order.orderProductCategories.length > 0">
                                <template #prefix>
                                    €
                                </template>
                            </text-input>
                        </template>
                    </input-group>

                    <input-group
                        :model-value="formatFloatValue(localOrder.transactionValue)"
                        @update:model-value="localOrder.transactionValue = parseFloatValue($event)"
                    >
                        <template #label>
                            <input-label>Transactieprijs</input-label>
                        </template>
                        <template #input>
                            <text-input :disabled="!props.editable">
                                <template #prefix>
                                    €
                                </template>
                            </text-input>
                        </template>
                    </input-group>

                    <input-group v-model="localOrder.productionDate">
                        <template #label>
                            <input-label>Productiedatum</input-label>
                        </template>
                        <template #input>
                            <date-input clearable :disabled="!props.editable">
                                <template #suffix>
                                    <svg-icon :icon="IconCalendarToday" :size="IconSize.sm" />
                                </template>
                            </date-input>
                        </template>
                    </input-group>

                    <input-group v-model="localOrder.transactionDate">
                        <template #label>
                            <input-label>Transportdatum</input-label>
                        </template>
                        <template #input>
                            <date-input clearable :disabled="!props.editable">
                                <template #suffix>
                                    <svg-icon :icon="IconCalendarToday" :size="IconSize.sm" />
                                </template>
                            </date-input>
                        </template>
                    </input-group>

                    <input-group v-model="localOrder.objectCode">
                        <template #label>
                            <input-label>Object code</input-label>
                        </template>
                        <template #input>
                            <text-input clearable :disabled="!props.editable" />
                        </template>
                    </input-group>
                </two-columns>

                <horizontal-line />

                <template v-if="order.orderProductCategories.length > 0">
                    <div>
                        <text-heading sub>
                            Omzet categorieën
                        </text-heading>

                        Voor rapportage doeleinden wordt de omzet verdeeld over categorien.<br />
                        Bedragen zijn afkomstig uit Realworks facturen.
                    </div>

                    <card-normal without-padding>
                        <list-container style="width: 100%">
                            <template #header>
                                <list-cell-header>
                                    <template #title>
                                        Omzet categorie
                                    </template>
                                </list-cell-header>
                                <list-cell-header>
                                    <template #title>
                                        Waarde
                                    </template>
                                </list-cell-header>
                            </template>
                            <template #content>
                                <list-row v-for="item in order.orderProductCategories" :key="item.id">
                                    <list-cell-text>
                                        <template #content>
                                            <text-decorator :color="Color.brandPurple">
                                                {{ item.productCategory?.name }}
                                            </text-decorator>
                                        </template>
                                    </list-cell-text>
                                    <list-cell-text>
                                        <template #content>
                                            <text-decorator bold>
                                                €{{ formatFloatValue(item.value) }}
                                            </text-decorator>
                                        </template>
                                    </list-cell-text>
                                </list-row>
                            </template>
                        </list-container>
                    </card-normal>

                    <horizontal-line />
                </template>

                <div>
                    <text-heading sub>
                        Verdeling omzet/courtage
                    </text-heading>
                    Voor rapportage kun je de omzetbijdrage verdelen over betrokken medewerkers.<br />
                    Bedragen zijn indicatief en aanpasbaar.
                </div>

                <card-normal>
                    <flex-container style="align-items: center">
                        <template v-for="share in orderCalculation.teamShares.value" :key="share.id">
                            <flex-container :size="5">
                                {{ share.branch?.name ?? 'Onbekend' }}
                            </flex-container>

                            <flex-container :size="5">
                                <input-group
                                    :model-value="formatFloatValue(share.orderValue)"
                                    @update:model-value="share.orderValue = parseFloatValue($event);"
                                >
                                    <template #input>
                                        <text-input :disabled="!props.editable">
                                            <template #suffix>
                                                <button
                                                    type="button"
                                                    style="all: unset"
                                                    @click="() => pickPercentage(share.id)"
                                                >
                                                    {{ orderCalculation.calculatePercentage(share.orderValue, orderCalculation.orderValue.value) }}&percnt;
                                                </button>
                                            </template>
                                        </text-input>
                                    </template>
                                </input-group>
                            </flex-container>
                        </template>
                    </flex-container>

                    <template #sections>
                        <card-section area="card-header">
                            <text-decorator :color="Color.brandPurple">
                                Omzet bijdrage voor team
                            </text-decorator>
                        </card-section>
                    </template>
                </card-normal>

                <p>
                    Restant toe te wijzen omzet bijdrage aan de betrokken medewerkers na aftrek van de omzet
                    bijdrage van het team:

                    <text-decorator bold>
                        &euro; {{ formatNumber(orderCalculation.userAvailableValue.value, 2) }}
                    </text-decorator>
                </p>

                <card-normal>
                    <div
                        v-for="share in orderCalculation.userShares.value"
                        :key="share.id"
                        class="user-share-grid"
                    >
                        <div>
                            {{ share.user?.name ?? 'Onbekend' }}
                        </div>

                        <div>
                            <input-group
                                :model-value="formatFloatValue(orderCalculation.calculatePercentage(share.orderValue, orderCalculation.userAvailableValue.value))"
                                @update:model-value="orderCalculation.setFromPercentage(
                                    share.id,
                                    parseFloatValue($event),
                                    orderCalculation.userAvailableValue.value,
                                );"
                            >
                                <template #input>
                                    <text-input :disabled="!props.editable">
                                        <template #suffix>
                                            %
                                        </template>
                                    </text-input>
                                </template>
                            </input-group>
                        </div>

                        <div>
                            <input-group
                                :model-value="formatFloatValue(share.orderValue)"
                                @update:model-value="share.orderValue = parseFloatValue($event);"
                            >
                                <template #input>
                                    <text-input :disabled="!props.editable">
                                        <template #prefix>
                                            €
                                        </template>
                                    </text-input>
                                </template>
                            </input-group>
                        </div>

                        <button
                            type="button"
                            style="all: unset"
                            :disabled="!props.editable"
                            @click="orderCalculation.removeUserEntry(share.id)"
                        >
                            <svg-icon
                                :icon="IconDelete"
                                :color="Color.gray400"
                            />
                        </button>
                    </div>

                    <template #sections>
                        <card-section area="card-header">
                            <div class="user-share-grid">
                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        Medewerkers
                                    </text-decorator>
                                </div>

                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        Courtage (%)
                                    </text-decorator>
                                </div>

                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        Courtage (&euro;)
                                    </text-decorator>
                                </div>
                            </div>
                        </card-section>
                        <card-section area="card-footer">
                            <div class="user-share-grid">
                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        Totaal verdeeld:
                                    </text-decorator>
                                </div>

                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        {{ formatNumber(orderCalculation.userTotalPercentage.value, 2) }}&percnt;
                                    </text-decorator>
                                </div>

                                <div>
                                    <text-decorator :color="Color.brandPurple">
                                        &euro; {{ formatNumber(orderCalculation.userTotalValue.value, 2) }}
                                    </text-decorator>
                                </div>
                            </div>
                        </card-section>
                    </template>
                </card-normal>

                <alert-block v-if="!orderCalculation.isAtTotalValue.value" :color="Color.orange500">
                    <template #prefix>
                        <svg-icon :icon="IconWarning" />
                    </template>
                    <template #default>
                        <span v-if="orderCalculation.isAboveTotalValue.value">
                            Er is teveel courtage verdeeld over de verschillende gebruikers. Gelieve dit handmatig aan te passen om door te gaan.
                        </span>

                        <span v-if="orderCalculation.isBelowTotalValue.value">
                            Er is te weinig courtage verdeeld over de verschillende gebruikers. Gelieve dit handmatig aan te passen om door te gaan.
                        </span>
                    </template>
                </alert-block>

                <flex-container v-if="order.assignedToBranch && props.editable" justify-content="center">
                    <button-text @click="addUserEntryDialog?.openOverlay()">
                        <svg-icon :icon="IconAdd" />
                        <span>Medewerker toevoegen</span>
                    </button-text>
                </flex-container>
            </flex-container>
        </flex-container>
    </card-normal>

    <modal-dialog
        ref="addUserEntryDialog"
        label="Medewerker toevoegen"
        description="Voeg een medewerker toe aan de verdeling van de omzet bijdrage."
        without-padding
        :size="3"
    >
        <flex-container :padding="Size.md" :gap="Size.md">
            <flex-container :gap="Size.xs">
                <text-heading>Medewerker toevoegen</text-heading>
                <p>Kies de gebruiker die je wilt toevoegen aan de verdeling van de omzet bijdrage.</p>
            </flex-container>

            <input-group
                v-model="selectedUser"
            >
                <template #input>
                    <user-select-input
                        :users="selectableUsers as User[]"
                    />
                </template>
            </input-group>
        </flex-container>

        <dialog-footer>
            <button-outline @click="addUserEntryDialogOnCancel">
                Annuleren
            </button-outline>

            <button-block @loading="addUserEntryDialogOnSave">
                Bevestig
            </button-block>
        </dialog-footer>
    </modal-dialog>
</template>

<script setup lang="ts">
    import {
        Color,
        CardNormal,
        FlexContainer,
        IconSize,
        InputGroup,
        InputLabel,
        SvgIcon,
        TextHeading,
        TextInput,
        DateInput,
        HorizontalLine,
        CardSection,
        TextDecorator,
        ButtonText,
        ButtonOutline,
        ButtonBlock,
        ModalDialog,
        AlertBlock,
        Size,
    } from '@keyvue/one-ui';
    import type { ExposedOverlay } from '@keyvue/one-ui';

    import TwoColumns from '@components/UI/TwoColumns.vue';
    import IconDelete from '@keyvue/one-material-icons/icons/delete.svg?component';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import IconWarning from '@keyvue/one-material-icons/icons/warning.svg?component';
    import IconCalendarToday from '@keyvue/one-material-icons/icons/calendar_today.svg?component';

    import { computed, ref, toRef, watch } from 'vue';
    import { Team, User } from '@keyvue/one-api-client';
    import type { TeamSubResource } from '@keyvue/one-api-client';
    import type { Order } from '@keyvue/one-api-client-legacy';
    import useOrderCalculation from '@composables/useOrderCalculation';
    import useClient from '@composables/useClient';
    import UserSelectInput from '@components/Inputs/UserSelectInput.vue';
    import { formatNumber } from '@/utils/number';
    import { OrderAPI } from '@keyvue/one-api-client-legacy';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        order: Order,
        editable?: boolean,
    }

    const props = defineProps<Props>();
    const localOrder = toRef(props, 'order');
    const eventBus = useEventBus();
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const { client } = useClient();

    const orderCalculation = useOrderCalculation(localOrder);

    const selectedUser = ref<User>();

    const assignedTeam = ref<Team | null>();

    const availableUsers = ref<{ data: User[], total: number }>({ data: [], total: 0 });

    const addUserEntryDialog = ref<ExposedOverlay>();

    const loadUsers = async () => {
        if (localOrder.value.assignedToBranch) {
            assignedTeam.value = await client.get(Team, localOrder.value.assignedToBranch.id);
            availableUsers.value = await client.getSubResources<TeamSubResource, User>(
                assignedTeam.value,
                User,
                'available-users',
            );
        }
    };

    await loadUsers();

    const selectableUsers = computed(() => {
        return availableUsers.value.data.filter((availableUser) => {
            return !orderCalculation.userShares.value.some((share) => share.user?.id === availableUser.id.value);
        });
    });

    const updateOrderValue = (value: any) => {
        if (value === orderCalculation.orderValue.value) {
            return;
        }

        localOrder.value.orderValue = value;
    };

    const formatFloatValue = (value: any) => {
        const newValue = Number.parseFloat(value);

        if (Number.isNaN(newValue)) {
            return '';
        }

        return formatNumber(newValue, 2);
    };

    const parseFloatValue = (value: any) => {
        const newValue = Number.parseFloat(
            value.toString()
                .replaceAll('.', '')
                .replaceAll(',', '.'),
        );

        if (Number.isNaN(newValue)) {
            return 0;
        }

        return newValue;
    };

    const addUserEntryDialogOnCancel = () => {
        addUserEntryDialog.value?.closeOverlay();
        selectedUser.value = undefined;
    };

    const addUserEntryDialogOnSave = (event: any) => {
        if (!selectedUser.value) {
            return;
        }

        orderCalculation.addUserEntry(selectedUser.value, 0);
        event.button.setState('completed');
        addUserEntryDialog.value?.closeOverlay();
        selectedUser.value = undefined;
    };

    const pickPercentage = (id: string) => {
        // @todo: zelfde als in legacy voor nu
        // eslint-disable-next-line no-alert
        const value = prompt('Welk percentage wil je instellen?');

        if (!Number.isNaN(value)) {
            orderCalculation.setFromPercentage(id, parseFloatValue(value), orderCalculation.orderValue.value);
        }
    };

    const onOrderReset = async () => {
        eventBus.dispatch('resource-manipulated', 'Order');
    };

    const onOrderSave = async (event: any) => {
        await saveOrder();
        event.button.setState('completed');
    };

    const saveOrder = async () => {
        const closeNotification = notifySaving(undefined, 0);
        try {
            await OrderAPI.update(localOrder.value.id, localOrder.value as Order);
            eventBus.dispatch('resource-manipulated', 'Order');
            notifySaved();
        } catch (error) {
            notifyError(error);
        } finally {
            if (closeNotification) {
                closeNotification();
            }
        }
    };

    watch(props.order, async () => {
        localOrder.value = props.order;

        await loadUsers();
    });
</script>

<style scoped lang="scss">
.financial-tab-panel__fields {
    width: 100%;
    line-height: 125%;
}

.user-share-grid {
    width: 100%;
    display: grid;
    grid-template-columns: auto 20% 20% 24px;
    align-items: center;
    gap: 20px;
    overflow: hidden;
}

.user-share-grid + .user-share-grid {
    margin-top: 10px;
}
</style>
